import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import type { DocumentNode } from 'graphql'
import { GET_CONTENT } from './query'
import { GET_COURSE_MODULES } from '../detail/query'
import prepareGetContentWriteQueryData from './prepareGetContentCacheData'
import restoreApolloSearchPageSeeds from '../community/oracle/restoreApolloSearchPageSeeds'

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function restoreApolloGetContentSeed (
  client: ApolloClient<NormalizedCacheObject>,
  seed: unknown,
  query: DocumentNode,
) {
  if (!isRecord(seed)) {
    return
  }
  const { apollo } = seed
  if (!isRecord(apollo) || !apollo.data || !apollo.variables) {
    return
  }
  const preparedData = prepareGetContentWriteQueryData(apollo.data)
  if (!preparedData) {
    return
  }
  try {
    client.writeQuery({
      query,
      variables: apollo.variables as Record<string, unknown>,
      data: preparedData,
    })
  } catch {
    // Cache seed is best-effort; client still falls back to network.
  }
}

/**
 * Apollo cache seeds for SSR GET_CONTENT (detail hero, member-home fallbacks).
 */
export function restoreApolloContentSeed (
  client: ApolloClient<NormalizedCacheObject>,
  seed: unknown,
) {
  restoreApolloGetContentSeed(client, seed, GET_CONTENT)
}

export function restoreApolloCourseModulesSeed (
  client: ApolloClient<NormalizedCacheObject>,
  seed: unknown,
) {
  restoreApolloGetContentSeed(client, seed, GET_COURSE_MODULES)
}

export function restoreApolloContentSeeds (
  client: ApolloClient<NormalizedCacheObject>,
  hydrate: Record<string, unknown> | undefined,
) {
  restoreApolloContentSeed(client, hydrate?.detailHero)
  restoreApolloCourseModulesSeed(client, hydrate?.courseModules)
  restoreApolloContentSeed(client, hydrate?.spotlightHero)

  const tileRows = hydrate?.memberHomeTileRows
  if (Array.isArray(tileRows)) {
    tileRows.forEach((seed) => {
      restoreApolloContentSeed(client, { apollo: isRecord(seed) ? seed.apollo : undefined })
    })
  }

  restoreApolloSearchPageSeeds(client, hydrate)
}
